import { KartoyIcon, type KartoyIconName } from '@mobile/components/kartoy/kartoy-icon'
import { GTextAnimated } from '@shared-ui/components/ui/text'
import { useAppTheme, useCurrentColorPalette } from '@shared-ui/theme'
import { rh } from '@shared-ui/theme/scale'
import { memo, type ReactNode, useEffect } from 'react'
import { Pressable, type StyleProp, type ViewStyle } from 'react-native'
import Animated, { interpolateColor, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'

import { useCreateStyles } from './styles'

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)
const AnimatedLabelText = GTextAnimated

type PillButtonVariant = 'primary' | 'secondary' | 'destructive'

type IconSlotProps = {
  iconStyle: StyleProp<ViewStyle> | ReturnType<typeof useAnimatedStyle>
  icon: KartoyIconName
  size: number
}

// Слот иконки кнопки с анимированной прозрачностью.
function IconSlot({ iconStyle, icon, size }: IconSlotProps) {
  const palette = useCurrentColorPalette()
  return (
    <Animated.View style={iconStyle}>
      <KartoyIcon icon={icon} size={size} isInverted={palette === 'dark'} />
    </Animated.View>
  )
}

export type PillButtonProps = {
  title: string
  onPress: () => void
  variant?: PillButtonVariant
  icon?: KartoyIconName
  iconNode?: ReactNode
  height?: number
  disabled?: boolean
  style?: StyleProp<ViewStyle>
  testID?: string
}

// Pill-кнопка kartoy: цвета — из глобальных токенов темы (primary = inverse-фон, secondary = surface).
// Press: мягкий scale-down; смена активности — плавная интерполяция цветов.
export const PillButton = memo(function PillButtonComponent({
  title,
  onPress,
  variant = 'primary',
  icon,
  iconNode,
  height = 52,
  disabled = false,
  style,
  testID,
}: PillButtonProps) {
  const theme = useAppTheme()
  const styles = useCreateStyles()
  const press = useSharedValue(0)
  const active = useSharedValue(disabled ? 0 : 1)

  useEffect(() => {
    active.value = withTiming(disabled ? 0 : 1, { duration: 220 })
  }, [active, disabled])

  const isPrimary = variant === 'primary'
  const isDestructive = variant === 'destructive'
  const isCompact = height <= 40
  let iconSize = isCompact ? 20 : 24

  if (height <= 32) iconSize = 16

  // Размеры считаем на JS-стороне: rw/rh — не ворклеты, в UI-потоке их звать нельзя
  const buttonHeight = rh(height)
  let activeBackground = isPrimary ? theme.COLOR_BG_INVERSE : theme.COLOR_BG_SURFACE
  let activeText = isPrimary ? theme.COLOR_TEXT_INVERSE_PRIMARY : theme.COLOR_TEXT_PRIMARY

  if (isDestructive) {
    activeBackground = theme.COLOR_BG_CRITICAL_PRIMARY
    activeText = theme.COLOR_TEXT_FIXED
  }

  const containerStyle = useAnimatedStyle(() => {
    const pressedScale = 1 - press.value * 0.03

    return {
      backgroundColor: interpolateColor(active.value, [0, 1], [theme.COLOR_BG_DISABLED, activeBackground]),
      height: buttonHeight,
      transform: [{ scale: pressedScale }],
    }
  }, [press, active, theme.COLOR_BG_DISABLED, activeBackground, buttonHeight])

  const textStyle = useAnimatedStyle(
    () => ({
      color: interpolateColor(active.value, [0, 1], [theme.COLOR_TEXT_DISABLED, activeText]),
    }),
    [active, theme.COLOR_TEXT_DISABLED, activeText],
  )

  const iconStyle = useAnimatedStyle(() => ({
    opacity: 0.4 + active.value * 0.6,
  }))

  return (
    <AnimatedPressable
      testID={testID}
      accessibilityLabel={title}
      accessibilityRole="button"
      disabled={disabled}
      onPress={onPress}
      onPressIn={() => {
        press.value = withTiming(1, { duration: 110 })
      }}
      onPressOut={() => {
        press.value = withTiming(0, { duration: 160 })
      }}
      style={[styles.button, isCompact && styles.compact, height <= 32 && styles.small, containerStyle, style]}
    >
      {icon ? <IconSlot icon={icon} iconStyle={iconStyle} size={iconSize} /> : null}
      {iconNode}
      <AnimatedLabelText
        variant={isCompact ? 'caption-14-semibold' : 'body-16-semibold'}
        style={[styles.label, textStyle]}
      >
        {title}
      </AnimatedLabelText>
    </AnimatedPressable>
  )
})
